import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import type { FormState } from '@de/providers/DataSourceManager.types';
import {
  observedFormStatesEqual,
  selectObservedFormState,
} from '@de/utils/observed-form-state/selectObservedFormState';

/**
 * Public form-state change. `formState` holds only the keys the host
 * declared in `observedFormStateKeys`, each taken from this step's snapshot.
 */
export interface ObservedFormStateChange {
  readonly stepId: string;
  readonly formState: FormState;
}

export type ObservedFormStateChangeHandler = (
  change: ObservedFormStateChange,
) => void;

interface ObservedFormStateSubscription {
  readonly keysRef: React.MutableRefObject<readonly string[]>;
  readonly onChangeRef: React.MutableRefObject<
    ObservedFormStateChangeHandler | undefined
  >;
}

const EMPTY_OBSERVED_KEYS: readonly string[] = [];

const defaultSubscription: ObservedFormStateSubscription = {
  keysRef: { current: EMPTY_OBSERVED_KEYS },
  onChangeRef: { current: undefined },
};

const ObservedFormStateContext =
  createContext<ObservedFormStateSubscription>(defaultSubscription);

interface ObservedFormStateProviderProps {
  readonly observedFormStateKeys?: readonly string[];
  readonly onFormStateChange?: ObservedFormStateChangeHandler;
  readonly children: React.ReactNode;
}

/**
 * Host subscription for form-state changes.
 *
 * An empty key list is the default and emits nothing. The listener is
 * stored in a ref so a new function identity on each host render does
 * not rebuild the experience.
 */
export const ObservedFormStateProvider: React.FC<
  ObservedFormStateProviderProps
> = ({
  observedFormStateKeys = EMPTY_OBSERVED_KEYS,
  onFormStateChange,
  children,
}) => {
  const keysRef = useRef(observedFormStateKeys);
  keysRef.current = observedFormStateKeys;
  const onChangeRef = useRef(onFormStateChange);
  onChangeRef.current = onFormStateChange;
  const value = useMemo<ObservedFormStateSubscription>(
    () => ({ keysRef, onChangeRef }),
    [],
  );

  return (
    <ObservedFormStateContext.Provider value={value}>
      {children}
    </ObservedFormStateContext.Provider>
  );
};

const baselineSignature = (
  stepId: string | undefined,
  observedKeys: readonly string[],
): string => `${stepId ?? ''}\0${observedKeys.join('\0')}`;

/**
 * Report a committed form-state snapshot to the host.
 *
 * The first projection for a step is the seed, not a change: the host
 * already applied `initialFormState` before mount. A later commit emits
 * only when a declared key's value differs from that baseline, whether a
 * learner changed it or the experience wrote it (IP country, auto-population).
 * Text inputs are not debounced here — one `updateFormState` is one event.
 *
 * The baseline is stored on the first commit for that step, not during
 * render, so a render React discards cannot move it.
 */
export const useEmitObservedFormStateChange = (
  seededFormState: FormState,
): ((activeStepId: string, snapshot: FormState) => void) => {
  const subscription = useContext(ObservedFormStateContext);
  const baselineRef = useRef<FormState>({});
  const baselineKeyRef = useRef<string | undefined>(undefined);
  const seededRef = useRef(seededFormState);
  seededRef.current = seededFormState;

  return useCallback(
    (activeStepId: string, snapshot: FormState) => {
      const observedKeys = subscription.keysRef.current;
      const activeSignature = baselineSignature(activeStepId, observedKeys);
      if (baselineKeyRef.current !== activeSignature) {
        baselineKeyRef.current = activeSignature;
        baselineRef.current = selectObservedFormState(
          seededRef.current,
          observedKeys,
        );
      }

      const projected = selectObservedFormState(snapshot, observedKeys);
      if (observedFormStatesEqual(baselineRef.current, projected)) return;

      baselineRef.current = projected;
      if (observedKeys.length === 0) return;
      subscription.onChangeRef.current?.({
        stepId: activeStepId,
        formState: projected,
      });
    },
    [subscription],
  );
};
